/**
 * ProjectLlmSettingsModal — správa vlastních (BYOK) LLM profilů volajícího
 * uživatele. Profily patří uživateli (entity_id), NE projektu — jsou
 * dostupné ve všech jeho Codable projektech (stejný precedent jako Codex
 * OAuth připojení, acm.codable_user_connections).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Cpu, X } from 'lucide-react';
import { api } from '../../../lib/api';
import { BackButton } from '../../../components/ui';
import { type LlmProfile, LlmProfileEditor } from '../../../components/ui/LlmProfileEditor';
import { themedConfirm } from '../../../lib/themed-dialogs';

interface OwnProfileRow {
  id: string;
  label: string;
  backend: 'openai' | 'anthropic';
  url: string;
  model: string;
  api_key: string;
  enabled: boolean;
  priority: number;
}

interface Props {
  slug: string;
  onClose: () => void;
  onBack?: () => void;
}

function toLlmProfile(r: OwnProfileRow): LlmProfile {
  return {
    id: r.id,
    label: r.label,
    url: r.url,
    model: r.model,
    api_key: r.api_key,
    backend: r.backend,
    enabled: r.enabled,
    priority: r.priority,
    daily_token_limit: 0,
    source: 'own',
  };
}

export default function ProjectLlmSettingsModal({ slug, onClose, onBack }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const profilesQ = useQuery({
    queryKey: ['codable-own-llm-profiles'],
    queryFn: () =>
      api.mcp<{ ok: boolean; profiles: OwnProfileRow[] }>('codable.llm_own_profile_list', {}),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['codable-own-llm-profiles'] });

  const saveM = useMutation({
    mutationFn: (p: LlmProfile) =>
      api.mcp<{ ok: boolean; error?: string }>('codable.llm_own_profile_save', {
        id: p.id.startsWith('new_') ? undefined : p.id,
        label: p.label,
        backend: p.backend,
        url: p.url,
        model: p.model,
        api_key: p.api_key,
        enabled: p.enabled,
        priority: p.priority,
      }),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('codable.saveFailed', 'Uložení selhalo'));
        return;
      }
      toast.success(t('codable.saved', 'Uloženo'));
      invalidate();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.mcp<{ ok: boolean }>('codable.llm_own_profile_delete', { id }),
    onSuccess: () => {
      toast.success(t('codable.deletedShort', 'Smazáno'));
      invalidate();
    },
  });

  const rows = profilesQ.data?.profiles ?? [];

  const toggleM = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => {
      const existing = rows.find((p) => p.id === id);
      if (!existing) return Promise.resolve({ ok: false });
      return api.mcp('codable.llm_own_profile_save', {
        id,
        label: existing.label,
        backend: existing.backend,
        url: existing.url,
        model: existing.model,
        api_key: '***',
        enabled,
        priority: existing.priority,
      });
    },
    onSuccess: invalidate,
  });

  const reorderM = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      await Promise.all(orderedIds.map((id, i) => {
        const existing = rows.find((p) => p.id === id);
        if (!existing) return Promise.resolve();
        return api.mcp('codable.llm_own_profile_save', {
          id,
          label: existing.label,
          backend: existing.backend,
          url: existing.url,
          model: existing.model,
          api_key: '***',
          enabled: existing.enabled,
          priority: i,
        });
      }));
    },
    onSuccess: invalidate,
  });

  const profiles = rows.map(toLlmProfile);

  const addProfile = () => {
    saveM.mutate({
      id: `new_${Date.now()}`,
      label: t('codable.newModel', 'Nový model'),
      url: '',
      model: '',
      api_key: '',
      backend: 'openai',
      enabled: true,
      priority: profiles.length,
      daily_token_limit: 0,
    });
  };

  return (
    <div className='fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4'>
      <div className='bg-gray-900 border border-gray-800 rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto space-y-4'>
        <div className='flex items-center justify-between gap-3'>
          {onBack
            ? (
              <BackButton
                onClick={onBack}
                label={t('codable.wsSettings')}
                crumb={t('codable.myLlmModels', 'Moje LLM modely')}
              />
            )
            : (
              <h3 className='font-bold text-white text-lg flex items-center gap-2 min-w-0'>
                <Cpu size={20} className='text-blue-400 shrink-0' />
                {t('codable.myLlmModels', 'Moje LLM modely')}
              </h3>
            )}
          <button onClick={onClose} className='text-gray-500 hover:text-gray-200 shrink-0'>
            <X size={20} />
          </button>
        </div>

        <p className='text-xs text-gray-500'>
          {t(
            'codable.ownByokHintBefore',
            'Vlastní API klíč (BYOK) — patří tobě, ne projektu',
          )}{' '}
          <span className='font-mono'>{slug}</span>{' '}
          {t(
            'codable.ownByokHintAfter',
            '— je dostupný ve všech tvých Codable projektech.',
          )}
        </p>

        <LlmProfileEditor
          profiles={profiles}
          onSave={(p) => saveM.mutate(p)}
          onDelete={async (id) => {
            if (await themedConfirm(t('codable.deleteModelConfirm', 'Smazat tento model?'))) deleteM.mutate(id);
          }}
          onToggle={(id, enabled) => toggleM.mutate({ id, enabled })}
          onReorder={(ids) => reorderM.mutate(ids)}
          onAdd={addProfile}
        />
      </div>
    </div>
  );
}
